import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cx } from "../presentation.tsx";
import { Icon, type IconName } from "../icons.tsx";

export interface HeroHeaderProps {
  /** Small line above the title: a date, a site name. */
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Before the text: a Mark or an Avatar. */
  leading?: ReactNode;
  /** Top-right: an avatar, an icon button, a sync pill. */
  trailing?: ReactNode;
  /** Under the text: usually two ActionTiles with tone "glass". */
  children?: ReactNode;
  /** "brand": the evergreen band with the mark's arcs; "plain": text on the page. */
  tone?: "brand" | "plain";
  /** Heading level of the title (default h1). */
  level?: 1 | 2;
  className?: string;
}

/**
 * The top of a home screen: a greeting or a place, with the primary actions right under it.
 * The brand tone is a gradient band (hero-bg to hero-bg-2) with the mark's two heads drawn large
 * and faint in the corner; text on it is on-hero and hero-muted (both contrast-checked).
 */
export function HeroHeader({ eyebrow, title, subtitle, leading, trailing, children, tone = "brand", level = 1, className }: HeroHeaderProps) {
  const H = level === 1 ? "h1" : "h2";
  return (
    <header className={cx("pmu-hero", `pmu-hero--${tone}`, className)}>
      {tone === "brand" && (
        <svg className="pmu-hero__art" viewBox="0 0 200 160" aria-hidden="true" focusable="false">
          <circle cx="92" cy="58" r="30" /><circle cx="160" cy="80" r="20" />
          <path d="M40 124c22 44 124 52 150 0" fill="none" strokeWidth="18" strokeLinecap="round" />
        </svg>
      )}
      <div className="pmu-hero__row">
        {leading && <div className="pmu-hero__leading">{leading}</div>}
        <div className="pmu-hero__text">
          {eyebrow && <p className="pmu-hero__eyebrow">{eyebrow}</p>}
          <H className="pmu-hero__title">{title}</H>
          {subtitle && <p className="pmu-hero__subtitle">{subtitle}</p>}
        </div>
        {trailing && <div className="pmu-hero__trailing">{trailing}</div>}
      </div>
      {children && <div className="pmu-hero__body">{children}</div>}
    </header>
  );
}

export interface ActionTileProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "title"> {
  icon: IconName;
  title: ReactNode;
  description?: ReactNode;
  /** primary: brand fill. secondary: a card. glass: translucent, for use on a HeroHeader. accent: marigold. */
  tone?: "primary" | "secondary" | "glass" | "accent";
  /** stack: icon above the text (grids of two). row: icon, text, chevron (lists). */
  layout?: "stack" | "row";
}

/** A large, confident action: "Drop off", "Pick up", "Someone is collecting". A real <button>. */
export function ActionTile({ icon, title, description, tone = "secondary", layout = "stack", className, type = "button", ...rest }: ActionTileProps) {
  return (
    <button {...rest} type={type} className={cx("pmu-tile", `pmu-tile--${tone}`, `pmu-tile--${layout}`, className)}>
      <span className="pmu-tile__icon"><Icon name={icon} /></span>
      <span className="pmu-tile__text">
        <span className="pmu-tile__title">{title}</span>
        {description && <span className="pmu-tile__description">{description}</span>}
      </span>
      {layout === "row" && <Icon name="chevron-right" className="pmu-tile__chevron" />}
    </button>
  );
}

export type StatTone = "neutral" | "brand" | "success" | "info" | "warning" | "accent";

export interface StatTileProps {
  value: ReactNode;
  label: ReactNode;
  tone?: StatTone;
  icon?: IconName;
  className?: string;
}

/** A count and what it counts ("4 here"). Tabular figures; the tone is a dot, the label carries meaning. */
export function StatTile({ value, label, tone = "neutral", icon, className }: StatTileProps) {
  return (
    <div className={cx("pmu-stat", `pmu-stat--${tone}`, className)}>
      <span className="pmu-stat__value">{value}</span>
      <span className="pmu-stat__label">{icon ? <Icon name={icon} /> : <span className="pmu-stat__dot" aria-hidden="true" />}{label}</span>
    </div>
  );
}
